import { Container, Row, Col, Card, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import type { UserOrganisation } from '../types/types';
import { useSupplierAssessments } from '../hooks/useSupplierAssessments';
import { Link } from 'react-router-dom';
import 'react-circular-progressbar/dist/styles.css';

const complianceStyles = {
  hero: {
    border: '1px solid #dbe4ee',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.06)',
    background: '#ffffff',
  },
  heroHeader: {
    background:
      'linear-gradient(135deg, #ffffff 0%, #fafcff 60%, #f3f6f9 100%)',
    borderBottom: '1px solid #dbe4ee',
  },
  statCard: {
    border: '1px solid #dbe4ee',
    borderRadius: '20px',
    background: '#ffffff',
    boxShadow: '0 10px 24px rgba(15, 23, 42, 0.05)',
  },
  panel: {
    border: '1px solid #dbe4ee',
    borderRadius: '22px',
    background: '#ffffff',
    boxShadow: '0 14px 32px rgba(15, 23, 42, 0.05)',
    overflow: 'hidden',
  },
  panelHeader: {
    background: '#f8fbfd',
    borderBottom: '1px solid #e2e8f0',
  },
} satisfies Record<string, React.CSSProperties>;

const MyCompliance = () => {
  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const { complianceOverview, overviewLoading, error } = useSupplierAssessments(
    selectedOrganisation?.organisation?.id,
  );

  const overview = complianceOverview;
  const averageCompliance = overview?.average_compliance_percentage;

  const hasAiCompliancePlan =
    selectedOrganisation?.organisation?.plan?.features?.['aiCompliancePlan'];

  return (
    <Container fluid>
      <Card style={complianceStyles.hero} className="mb-4">
        <div style={complianceStyles.heroHeader} className="p-4 p-lg-5">
          <Row className="g-4 align-items-stretch">
            <Col lg={7}>
              <div className="h-100 d-flex flex-column justify-content-center">
                <div
                  className="text-uppercase fw-semibold mb-2"
                  style={{
                    letterSpacing: '0.12em',
                    fontSize: '0.78rem',
                    color: '#64748b',
                  }}
                >
                  Compliance Workspace
                </div>
                <h3 className="mb-2">My Compliance</h3>
                <p className="text-muted mb-0" style={{ maxWidth: '42rem' }}>
                  Track overall compliance performance, identify weak spots, and
                  move into the detailed overview when you need section,
                  evidence, and findings data.
                </p>
              </div>
            </Col>
            <Col lg={5}>
              <div className="h-100">
                <div
                  className="rounded-4 border p-3 p-lg-4 bg-white h-100 d-flex flex-column justify-content-between"
                  style={{ borderColor: '#dbe4ee' }}
                >
                  <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                    <div>
                      <div className="text-muted small mb-1">Organisation</div>
                      <div className="fw-semibold fs-6">
                        {selectedOrganisation?.organisation?.name ||
                          'Current organisation'}
                      </div>
                      {typeof averageCompliance === 'number' && (
                        <div className="text-muted small mt-2">
                          Average Compliance
                        </div>
                      )}
                    </div>
                    {overviewLoading ? (
                      <Spinner animation="border" />
                    ) : (
                      typeof averageCompliance === 'number' && (
                        <div className="d-flex justify-content-center flex-shrink-0 mx-auto mx-sm-0">
                          <div style={{ width: '92px', height: '92px' }}>
                            <CircularProgressbar
                              value={averageCompliance}
                              text={`${averageCompliance}%`}
                              styles={buildStyles({
                                pathColor:
                                  averageCompliance >= 75
                                    ? '#198754'
                                    : averageCompliance >= 50
                                      ? '#f59e0b'
                                      : '#dc3545',
                                trailColor: '#E2E8F0',
                                textColor: '#0f172a',
                                textSize: '16px',
                              })}
                            />
                          </div>
                        </div>
                      )
                    )}
                  </div>
                  <div
                    className="mt-3 pt-2 border-top"
                    style={{ borderColor: '#edf2f7' }}
                  >
                    <Link
                      to="/my-compliance/overview"
                      className="btn btn-outline-primary w-100"
                    >
                      View compliance overview
                    </Link>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </Card>

      <Row className="g-4 mb-4">
        <Col md={6} xl={4}>
          <Card style={complianceStyles.panel} className="h-100">
            <div style={complianceStyles.panelHeader} className="p-3 p-md-4">
              <h5 className="mb-1">AI Compliance Action Plan</h5>
              <div className="text-muted small">Module</div>
            </div>
            <Card.Body className="p-3 p-md-4 d-flex flex-column">
              {hasAiCompliancePlan ? (
                <>
                  <p className="mb-3">
                    Build your own AI-generated compliance action plan based on
                    your assessment outcomes and track remediation progress in
                    one place.
                  </p>
                  <div className="mt-auto">
                    <Link
                      to="/my-compliance/ai-plan"
                      className="btn btn-primary w-100"
                    >
                      Open AI compliance plan
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="mb-3">
                    Upgrade to our AI Compliance Plan to unlock advanced
                    insights and personalized recommendations powered by AI.
                    Contact your administrator to learn more about the benefits
                    and how to upgrade.
                  </p>
                  <div className="mt-auto">
                    <Link
                      to="/settings?tab=organisation"
                      className="btn btn-outline-primary w-100"
                    >
                      Upgrade Now
                    </Link>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {error && <p className="text-danger">Error: {error}</p>}
    </Container>
  );
};

export default MyCompliance;
